import { apiRequest } from './client';

/**
 * The two endpoints that describe the deployment itself rather than its content:
 * what build this is, and what it can do.
 *
 * Both are public, both are tiny, and both are read once at boot — so they are cached here
 * rather than re-fetched. The About panel and the capability gates ask the same question and
 * must never get different answers.
 */

export interface ReleaseNote {
  version: string;
  date: string;
  headline: string;
  items: string[];
}

export interface VersionInfo {
  version: string;
  releases: ReleaseNote[];
}

export type AiProvider = 'cloud' | 'local' | 'none';

export interface Capabilities {
  ai: {
    provider: AiProvider;
    available: boolean;
    docgen: boolean;
    translate: boolean;
    liveVerify: boolean;
    chat: boolean;
  };
}

let versionPromise: Promise<VersionInfo> | null = null;
let capabilitiesPromise: Promise<Capabilities> | null = null;

export function fetchVersion(): Promise<VersionInfo> {
  // Cached on the PROMISE, not the result, so two callers racing at boot make one request.
  versionPromise ??= apiRequest<VersionInfo>('/api/version').catch((err) => {
    versionPromise = null; // never cache a failure — the next attempt should retry
    throw err;
  });
  return versionPromise;
}

export function fetchCapabilities(): Promise<Capabilities> {
  capabilitiesPromise ??= apiRequest<Capabilities>('/api/capabilities').catch((err) => {
    capabilitiesPromise = null;
    throw err;
  });
  return capabilitiesPromise;
}

/** For tests, and for a locale switch that needs everything re-read. */
export function resetMetaCache(): void {
  versionPromise = null;
  capabilitiesPromise = null;
}

/**
 * Is this a connected deployment, or an offline one?
 *
 * Derived rather than stored: `provider` says what was configured and `available` says
 * whether it resolved, and only both together mean the AI features are really there.
 * `local` is reserved for Offline Phase 2 and currently resolves to unavailable.
 */
export const isConnectedDeployment = (caps: Capabilities | null): boolean =>
  !!caps && caps.ai.provider === 'cloud' && caps.ai.available;
